import { useTranslation } from 'react-i18next';
import { FOCUSRING } from '../focus';
import type { EigenVoorbeeld } from '../work-quality.api';

/**
 * Het voorbeeld op de Aandachtskaart: de productnaam (story 17) en de oude en de nieuwe waarde, in een kader, zonder productnummer, GTIN,
 * datum of nakijker (ux-spec 3.3 regel 5, besluit 19-10 #3).
 */
export interface ExamplePairProps {
  voorbeeld: EigenVoorbeeld;
}

/** Een tekst uit het antwoord die er ook echt een is; alles anders is afwezig, niet leeg. */
function tekst(waarde: unknown): string | null {
  return typeof waarde === 'string' && waarde.trim() !== '' ? waarde : null;
}

/**
 * De drie redenen die de eigenaar heeft goedgekeurd, letterlijk zoals dqs-service ze stuurt
 * (`PRODUCTLINK_REDENEN`, besluiten 14 en 23), met hun vertaalsleutel. Vertaald op de tekst en niet
 * op `grond`: die twee zijn niet één-op-één ("gevonden" zonder linkbasis geeft ook "geen
 * productpagina beschikbaar"). Een andere tekst komt ongewijzigd op het scherm.
 */
const REDENSLEUTELS = new Map([
  ['andere dienst', 'qa.eigen.productlinkReden.andereDienst'],
  ['niet eenduidig', 'qa.eigen.productlinkReden.nietEenduidig'],
  ['geen productpagina beschikbaar', 'qa.eigen.productlinkReden.geenProductpagina'],
]);

/** Een `http(s)`-adres met een host, ongewijzigd; al het andere is geen link (de `href` komt uit een antwoord). */
function geldigeLink(waarde: unknown): string | null {
  if (typeof waarde !== 'string') return null;
  try {
    const url = new URL(waarde);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.host !== '' ? waarde : null;
  } catch {
    return null;
  }
}

/**
 * De link of de reden bij een voorbeeld (SPEC-feedback-productlink): een geldige link wint, anders een
 * niet-lege reden, anders niets — ook bij "niet gemeten" (een week van vóór meetversie 11).
 */
function linkOfReden(productlink: unknown): { link: string } | { reden: string } | null {
  if (typeof productlink !== 'object' || productlink === null) return null;
  const veld = productlink as { gemeten?: unknown; waarde?: unknown };
  if (veld.gemeten !== true || typeof veld.waarde !== 'object' || veld.waarde === null) return null;
  const waarde = veld.waarde as { link?: unknown; reden?: unknown };
  const link = geldigeLink(waarde.link);
  if (link !== null) return { link };
  const reden = tekst(waarde.reden);
  return reden === null ? null : { reden };
}

/**
 * De sleutel voor de oude of de nieuwe waarde: gevuld, leeg gelaten, of helemaal niet vastgelegd.
 *
 * Die derde is geen haarkloverij. "Je liet dit leeg" is een bewering over wat iemand deed; staat er
 * in het antwoord geen tekst maar een `null`, dan is dat niet gemeten en mag het scherm het niet
 * alsnog beweren. Dezelfde regel die elders "onbekend" oplevert in plaats van een nul.
 */
function waardeSleutel(waarde: unknown, leeg: string, onbekend: string): string {
  if (typeof waarde !== 'string') return onbekend;
  return waarde.trim() === '' ? leeg : '';
}

/**
 * Eén voorbeeldpaar: wat jij invulde, en wat het werd.
 *
 * Dit is waarom dit scherm bestaat. Een cijfer zonder voorbeeld is niet te duiden en niet te
 * bespreken; een voorbeeld leert iets, óók wanneer het cijfer erover niet bestaat omdat twee
 * beoordelaars over dit veld van mening verschillen.
 *
 * **Geen rood-groene diff.** De oude waarde staat doorgehaald en gedempt, de nieuwe vet; de schermlezer hoort
 * "oud" en "nieuw", niet de kleur. Rood is in deze module voorbehouden aan techniek: een
 * correctie is per definitie geen fout van de persoon, en een rode regel spreekt dat in één
 * oogopslag tegen. De oude waarde staat gedempt omdat zij de aanleiding is, niet het oordeel.
 */
export function ExamplePair({ voorbeeld }: ExamplePairProps) {
  const { t } = useTranslation();

  const oud = tekst(voorbeeld?.oudeWaarde);
  const nieuw = tekst(voorbeeld?.nieuweWaarde);
  const productId = tekst(voorbeeld?.productId);
  const uitkomst = linkOfReden(voorbeeld?.productlink);
  const redensleutel = uitkomst !== null && 'reden' in uitkomst ? REDENSLEUTELS.get(uitkomst.reden) : undefined;
  // Story 17 (CAP-11): alleen een gemeten, niet-lege naam; anders geen regel en geen plaatshouder.
  const productnaam = voorbeeld?.productnaam?.gemeten === true ? tekst(voorbeeld.productnaam.waarde) : null;

  const productlink = (
    <>
      {uitkomst !== null && 'link' in uitkomst && (
        // Een nieuw tabblad, zodat de week die iemand leest niet verdwijnt; de naam zegt dat. Er staat één
        // voorbeeld per kaart, en productnummer en GTIN staan nergens (EXPERIENCE.md Voorbeeldkader), ook niet
        // in de naam van de link.
        <p className="mt-2 text-[0.75rem]">
          <a
            className={`rounded-[4px] text-[var(--secondary-text)] underline underline-offset-2 max-sm:inline-block max-sm:py-3 [@media(pointer:coarse)]:inline-block [@media(pointer:coarse)]:py-3 ${FOCUSRING}`}
            data-testid="werkkwaliteit-voorbeeld-productlink"
            href={uitkomst.link}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t('qa.eigen.productlinkBekijk')} <span className="sr-only">{t('qa.rapportkaart.nieuwTabblad')}</span>
          </a>
        </p>
      )}
      {uitkomst !== null && 'reden' in uitkomst && (
        <p className="mt-2 text-[0.75rem] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-voorbeeld-productlink-reden">
          {redensleutel === undefined ? uitkomst.reden : t(redensleutel)}
        </p>
      )}
    </>
  );

  return (
    <div className="rounded-[8px] border border-[var(--border)] bg-[var(--background-muted)] p-3" data-testid={`werkkwaliteit-voorbeeldpaar-${productId ?? 'onbekend'}`}>
      {productnaam !== null && (
        // Een naam kan 101 tekens zonder spatie zijn: hij breekt af in plaats van het scherm te verbreden.
        <p className="font-semibold text-[0.8125rem] text-[var(--foreground)] [overflow-wrap:anywhere]">{productnaam}</p>
      )}
      <p className="text-[0.875rem] leading-relaxed">
        <span className="text-[var(--foreground-muted)] line-through" data-testid="werkkwaliteit-voorbeeld-oud">
          <span className="sr-only">{t('qa.eigen.voorbeeldOud')}</span>{' '}
          {oud ?? t(waardeSleutel(voorbeeld?.oudeWaarde, 'qa.eigen.voorbeeldOudLeeg', 'qa.eigen.voorbeeldOudOnbekend'))}
        </span>
        <span aria-hidden="true" className="mx-2 text-[var(--foreground-muted)]">
          →
        </span>
        <span className="font-bold text-[var(--foreground)]" data-testid="werkkwaliteit-voorbeeld-nieuw">
          <span className="sr-only">{t('qa.eigen.voorbeeldNieuw')}</span>{' '}
          {nieuw ?? t(waardeSleutel(voorbeeld?.nieuweWaarde, 'qa.eigen.voorbeeldNieuwLeeg', 'qa.eigen.voorbeeldNieuwOnbekend'))}
        </span>
      </p>
      {productlink}
    </div>
  );
}
